/**
 * Toestemmingen en betaalwijze (ontwerp 31, punt 9; sessie 73 blok 3):
 * drie tegels.
 * - Seintje zak bijna leeg: ja (kanaal, datum, bron), nee (ook "nee,
 *   ingetrokken 3 sep") of niet gevraagd.
 * - Nieuwsbrief: uit het register; anders het vinkje uit de checkout
 *   ("aangevinkt in checkout 14 mei") of "niet gevraagd".
 * - Betaalwijze: die van de laatste bestelling; een opgeslagen
 *   betaalwijze houden we nog niet bij.
 * Per toestemming kleine knopjes om de stand te zetten; na het zetten
 * ververst alleen de kaart van deze klant.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { v6Api } from "../../api";
import { kanaalWoord } from "../../Rechts/opmaak";
import { v6Sleutels } from "../../sleutels";
import { datumKort } from "../opmaak";
import type { KaartToestemming, KaartToestemmingen } from "../types";
import { NogNiet } from "./NogNiet";

type Soort = "seintje" | "nieuwsbrief";
interface Zet {
  soort: Soort;
  stand: "ja" | "nee";
  kanaal?: string;
}

/**
 * Het woord van de stand: "ja", "nee", "nee, ingetrokken 3 sep" of
 * "niet gevraagd".
 */
function standWoord(t: KaartToestemming): string | null {
  if (t.stand === "nee" && t.ingetrokken_op) return `nee, ingetrokken ${datumKort(t.ingetrokken_op)}`;
  return t.stand;
}

/**
 * De regel onder de stand. Bij "ja": kanaal en de datum van geven; bij
 * "nee" geen oud kanaal (ingetrokken staat al in het woord).
 */
function onderRegel(t: KaartToestemming): string {
  if (t.stand === "ja") {
    return [t.kanaal ? kanaalWoord(t.kanaal) : null, t.gegeven_op ? datumKort(t.gegeven_op) : null, t.bron]
      .filter(Boolean)
      .join(", ");
  }
  if (t.stand === "nee") {
    const moment = t.ingetrokken_op ? null : (t.gegeven_op ?? t.gevraagd_op);
    return [moment ? datumKort(moment) : null, t.bron].filter(Boolean).join(", ");
  }
  const moment = t.gegeven_op ?? t.gevraagd_op;
  return [t.kanaal ? kanaalWoord(t.kanaal) : null, moment ? datumKort(moment) : null, t.bron]
    .filter(Boolean)
    .join(", ");
}

export function Toestemmingen({
  klantId,
  toestemmingen,
  compact = false,
}: {
  klantId: string;
  toestemmingen: KaartToestemmingen;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const zet = useMutation({
    mutationFn: (z: Zet) =>
      v6Api.klantToestemming(
        klantId,
        z.soort,
        z.kanaal ? { stand: z.stand, kanaal: z.kanaal } : { stand: z.stand },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) }),
  });
  const bezig = zet.isPending;

  const { seintje, nieuwsbrief, checkout_vinkje_op, betaalwijze } = toestemmingen;
  const seintjeOnder = onderRegel(seintje);
  const brief =
    standWoord(nieuwsbrief) ??
    (checkout_vinkje_op ? `aangevinkt in checkout ${datumKort(checkout_vinkje_op)}` : "niet gevraagd");
  const briefOnder = nieuwsbrief.stand ? onderRegel(nieuwsbrief) : "";

  const knop = (woord: string, label: string, z: Zet, aan: boolean) => (
    <button
      key={woord}
      type="button"
      disabled={bezig}
      aria-pressed={aan}
      aria-label={label}
      className={aan ? "aan" : undefined}
      onClick={() => zet.mutate(z)}
    >
      {woord}
    </button>
  );

  return (
    <div className={`kk-toestemmingen${compact ? " compact" : ""}`} data-testid="toestemmingen">
      <div className="kk-cijfer" data-testid="tegel-seintje">
        <span className="l">Seintje zak bijna leeg</span>
        <b className="w">{standWoord(seintje) ?? "niet gevraagd"}</b>
        {seintjeOnder ? <span className="o">{seintjeOnder}</span> : null}
        <span className="kk-mini">
          {knop(
            "Ja, WhatsApp",
            "Seintje: ja, via WhatsApp",
            { soort: "seintje", stand: "ja", kanaal: "whatsapp" },
            seintje.stand === "ja" && seintje.kanaal === "whatsapp",
          )}
          {knop(
            "Ja, mail",
            "Seintje: ja, via mail",
            { soort: "seintje", stand: "ja", kanaal: "mail" },
            seintje.stand === "ja" && seintje.kanaal === "mail",
          )}
          {knop("Nee", "Seintje: nee", { soort: "seintje", stand: "nee" }, seintje.stand === "nee")}
        </span>
      </div>
      <div className="kk-cijfer" data-testid="tegel-nieuwsbrief">
        <span className="l">Nieuwsbrief</span>
        <b className="w">{brief}</b>
        {briefOnder ? <span className="o">{briefOnder}</span> : null}
        <span className="kk-mini">
          {knop("Ja", "Nieuwsbrief: ja", { soort: "nieuwsbrief", stand: "ja" }, nieuwsbrief.stand === "ja")}
          {knop("Nee", "Nieuwsbrief: nee", { soort: "nieuwsbrief", stand: "nee" }, nieuwsbrief.stand === "nee")}
        </span>
      </div>
      <div className="kk-cijfer" data-testid="tegel-betaalwijze">
        <span className="l">Betaalwijze</span>
        <b className="w">{betaalwijze ?? <NogNiet soort="onbekend" />}</b>
        <span className="o">laatste bestelling</span>
        <span className="o">
          opgeslagen: <NogNiet />
        </span>
      </div>
      {zet.isError ? (
        <p className="v6-fout" role="alert">
          Vastleggen lukte niet. Probeer het opnieuw.
        </p>
      ) : null}
    </div>
  );
}
